(() => {
  const DESKTOP_MQ = '(min-width: 990px)';

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const rafThrottle = (callback) => {
    let ticking = false;
    return (...args) => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        callback(...args);
      });
    };
  };

  onReady(() => {
    const headerSection = document.getElementById('shopify-section-header');
    const wrapper = headerSection?.querySelector('.header-wrapper');
    const header = headerSection?.querySelector('.header');
    if (!headerSection || !wrapper || !header) return;

    const pageType = wrapper.dataset.pageType || '';
    const headerTemplate = wrapper.dataset.headerTemplate || '2';
    const isProductWithVi = wrapper.dataset.productWithVi === 'true';
    const isHome = headerTemplate === '1';
    const isAlwaysWhiteTemplate = headerTemplate === '2';
    const isBrandTemplate = headerTemplate === '3';
    const drawerDetails = document.getElementById('Details-menu-drawer-container');
    const mq = window.matchMedia(DESKTOP_MQ);

    const setStateClass = (nextClass) => {
      wrapper.classList.remove('header--home-top', 'header--page-top', 'header--scrolled', 'header--brand');
      wrapper.classList.add(nextClass);
    };

    const setHeaderVisualState = () => {
      if (isBrandTemplate) {
        setStateClass('header--brand');
        return;
      }

      if (isAlwaysWhiteTemplate || isProductWithVi) {
        setStateClass('header--scrolled');
        return;
      }

      if (window.scrollY <= 5) {
        setStateClass('header--home-top');
      } else {
        setStateClass('header--scrolled');
      }
    };

    const getOpenPanelHeight = () => {
      if (!mq.matches) return 0;
      const openPanel = header.querySelector('.header__inline-menu details[open] > ul.header__submenu');
      if (!openPanel) return 0;
      return Math.max(0, Math.round(openPanel.getBoundingClientRect().height));
    };

    const updateHeaderMetrics = () => {
      const headerHeight = Math.round(wrapper.offsetHeight || wrapper.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--header-bottom', `${headerHeight}px`);
      document.documentElement.style.setProperty('--overlay-panel-height', `${getOpenPanelHeight()}px`);
    };

    const scheduleHeaderMetrics = rafThrottle(updateHeaderMetrics);
    const scheduleVisualState = rafThrottle(setHeaderVisualState);

    setHeaderVisualState();
    updateHeaderMetrics();

    window.addEventListener('scroll', scheduleVisualState, { passive: true });
    window.addEventListener('resize', () => {
      scheduleVisualState();
      scheduleHeaderMetrics();
    });
    window.addEventListener('load', scheduleHeaderMetrics);
    window.addEventListener('pageshow', () => {
      scheduleVisualState();
      scheduleHeaderMetrics();
    });
    if (window.ResizeObserver) {
      const headerResizeObserver = new ResizeObserver(scheduleHeaderMetrics);
      headerResizeObserver.observe(wrapper);
    }

    if (mq.matches) {
      const dropdowns = header.querySelectorAll('details:has(> ul.header__submenu)');
      let closeTimer = null;

      const closeAll = (except = null) => {
        dropdowns.forEach((details) => {
          if (except && details === except) return;
          details.removeAttribute('open');
        });
        scheduleHeaderMetrics();
      };

      const openOne = (details) => {
        clearTimeout(closeTimer);
        closeAll(details);
        details.setAttribute('open', '');
        scheduleHeaderMetrics();
      };

      const scheduleClose = (details) => {
        clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          details.removeAttribute('open');
          scheduleHeaderMetrics();
        }, 120);
      };

      dropdowns.forEach((details) => {
        const summary = details.querySelector('summary');
        const panel = details.querySelector('ul.header__submenu');
        if (!summary || !panel) return;

        summary.addEventListener('mouseenter', () => openOne(details));
        panel.addEventListener('mouseenter', () => openOne(details));
        summary.addEventListener('mouseleave', () => scheduleClose(details));
        panel.addEventListener('mouseleave', () => scheduleClose(details));
        summary.addEventListener('click', (event) => event.preventDefault());
      });

      header.addEventListener('mouseleave', () => {
        clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => closeAll(), 120);
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeAll();
      });

      const openObserver = new MutationObserver(scheduleHeaderMetrics);
      openObserver.observe(header, { subtree: true, attributes: true, attributeFilter: ['open'] });
    }

    if (drawerDetails) {
      const LOCK_CLASS = 'kowi-lock';
      let scrollY = 0;

      const lock = () => {
        if (document.documentElement.classList.contains(LOCK_CLASS)) return;
        scrollY = window.scrollY || document.documentElement.scrollTop || 0;
        document.documentElement.classList.add(LOCK_CLASS);
        document.body.classList.add(LOCK_CLASS);
        requestAnimationFrame(() => window.scrollTo(0, scrollY));
      };

      const unlock = () => {
        if (!document.documentElement.classList.contains(LOCK_CLASS)) return;
        document.documentElement.classList.remove(LOCK_CLASS);
        document.body.classList.remove(LOCK_CLASS);
        document.body.classList.remove('overflow-hidden-tablet', 'overflow-hidden-mobile');
        window.scrollTo(0, scrollY);
      };

      const syncDrawerLock = () => {
        if (drawerDetails.open) lock();
        else unlock();
      };

      drawerDetails.addEventListener('toggle', syncDrawerLock);
      document.addEventListener('click', (event) => {
        if (
          event.target.closest('[data-menu-drawer-close]') ||
          event.target.closest('.menu-drawer__close-button') ||
          event.target.closest('.menu-drawer__close') ||
          event.target.closest('.menu-drawer__utility-links a') ||
          event.target.closest('summary.header__icon--menu')
        ) {
          if (event.target.closest('[data-menu-drawer-close]')) {
            drawerDetails.open = false;
          }
          setTimeout(syncDrawerLock, 0);
        }
      });

      const drawerObserver = new MutationObserver(syncDrawerLock);
      drawerObserver.observe(drawerDetails, { attributes: true, attributeFilter: ['open'] });
      syncDrawerLock();
    }

    const searchDrawer = document.getElementById('SearchDrawer');
    if (searchDrawer) {
      const openButtons = document.querySelectorAll('[data-search-drawer-open]');
      const closeButtons = searchDrawer.querySelectorAll('[data-search-drawer-close]');
      const input = searchDrawer.querySelector('#SearchDrawerInput');
      const form = searchDrawer.querySelector('form');
      const allLink = searchDrawer.querySelector('[data-search-drawer-all]');
      const results = searchDrawer.querySelector('[data-predictive-search]');
      const predictiveSearchRoot = searchDrawer.querySelector('predictive-search');
      const searchBase = (window.routes && window.routes.search_url) ? window.routes.search_url : '/search';

      const hasResults = () => {
        const list = searchDrawer.querySelector('.predictive-search__results-list');
        return !!list?.querySelector('.predictive-search__item--link');
      };

      const syncAllLink = () => {
        if (!allLink || !input) return;
        const q = input.value.trim();
        if (!q || !hasResults()) {
          allLink.setAttribute('aria-hidden', 'true');
          allLink.style.display = 'none';
          return;
        }
        allLink.style.display = 'inline-flex';
        allLink.setAttribute('aria-hidden', 'false');
        allLink.href = `${searchBase}?q=${encodeURIComponent(q)}&options[prefix]=last`;
      };

      const openSearch = () => {
        searchDrawer.classList.add('is-open');
        searchDrawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('search-drawer-open');
        setTimeout(() => input?.focus(), 50);
      };

      const closeSearch = () => {
        searchDrawer.classList.remove('is-open');
        searchDrawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('search-drawer-open');
      };

      openButtons.forEach((button) => button.addEventListener('click', openSearch));
      closeButtons.forEach((button) => button.addEventListener('click', closeSearch));

      if (form) {
        form.addEventListener('submit', (event) => {
          if (!input?.value.trim()) {
            event.preventDefault();
            return;
          }

          const submittedFromButton = event.submitter && event.submitter.classList?.contains('search-drawer__submit');
          if (!submittedFromButton) {
            event.preventDefault();
            if (hasResults()) {
              results?.focus();
            } else {
              input?.dispatchEvent(new Event('input', { bubbles: true }));
              requestAnimationFrame(() => {
                if (predictiveSearchRoot?.getAttribute('results') === 'true') {
                  results?.focus();
                }
              });
            }
            return;
          }

          closeSearch();
        });
      }

      input?.addEventListener('input', syncAllLink);
      if (results) {
        const resultsObserver = new MutationObserver(syncAllLink);
        resultsObserver.observe(results, { childList: true, subtree: true });
      }

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeSearch();
      });

      syncAllLink();
    }

    headerSection.classList.remove('header--hidden');
    headerSection.classList.add('header--shown');
  });
})();
